/**
 * 本地「流历」引擎 —— 浏览器端用 lunar-typescript 计算流月/流日/流时
 *
 * 语义与后端 FlowCalendarAssembler（tyme4j，lunar 为其同源 TS 版）对齐：
 *  - 流年 Y 从立春 Y 起，12 个节令月（寅月..丑月）；
 *  - termStart/termEnd 用节令时刻，精确到分钟（yyyy-MM-dd HH:mm，秒截断，东八区墙钟，勿做时区换算）；
 *  - days：某节令月起止日（date(termStart) 含 → date(termEnd) 不含）逐日；
 *  - hours：某日 12 双小时（子..亥），起于当日 00:00 前 23:00 的早子时段锚点，
 *    时柱由「流日天干 五鼠遁」决定（库内对 00:01..22:01 整时采样即可得，避开 23:00 换日歧义）。
 *  - 流年 Y 的节令链种子取 1/20 12:00 之后的首个节，依赖不变式「大寒 Y 必早于该时刻」：
 *    若库在极端年份先算出 大寒 Y+1（种子落在次年），首节将错成 立春Y+1，12 段全错 →
 *    listJieSolar 枚举 13 节后守卫断言首节必为 立春，异常即抛错而非静默输出错误流月。
 */

import { Solar } from 'lunar-typescript';
import { getShishen } from './shishenEngine';
import type { Stem, Shishen } from './shishenEngine';

export interface FlowMonth {
  monthIndex: number;
  ganzhi: string;
  tianGan: string;
  diZhi: string;
  termName: string;
  termStart: string;
  termEnd: string;
  tianGanShen: string;
  tianGanShiShen?: string;
}

export interface FlowDay {
  date: string;
  ganzhi: string;
  tianGan: string;
  diZhi: string;
  tianGanShen: string;
}

export interface FlowHour {
  hourIndex: number;
  hourLabel: string;
  ganzhi: string;
  tianGan: string;
  diZhi: string;
  tianGanShen: string;
}

export interface FlowCalendarResult {
  year: number;
  liuYue: FlowMonth[];
  days: FlowDay[];
  hours: FlowHour[];
}

export interface ComputeFlowCalendarOpts {
  /** 目标流年 */
  year: number;
  /** 命主日主天干（用于十神） */
  dayStem: Stem;
  /** 节令月 1..12（给定时返回该月逐日 days） */
  monthIndex?: number;
  /** yyyy-MM-dd（给定时返回该日 12 双小时 hours，需落在对应节令月内） */
  day?: string;
}

const HOUR_LABELS = ['子', '丑', '寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥'] as const;

/** getShishen 理论上不会返回「日主」，此处收窄为实际十干关系值 */
type PreciseShen = Exclude<Shishen, '日主'>;

/** 精确十神 → 五类粗分 */
const COARSE_GROUP: Record<PreciseShen, string> = {
  比肩: '比劫',
  劫财: '比劫',
  食神: '食伤',
  伤官: '食伤',
  偏财: '财星',
  正财: '财星',
  七杀: '官杀',
  正官: '官杀',
  偏印: '印枭',
  正印: '印枭',
};

function coarseOf(precise: PreciseShen): string {
  return COARSE_GROUP[precise];
}

function splitGanzhi(ganzhi: string): { tianGan: Stem; diZhi: string } {
  return { tianGan: ganzhi[0] as Stem, diZhi: ganzhi[1] };
}

/** 精确到分钟：yyyy-MM-dd HH:mm（秒截断） */
function formatMinute(solar: Solar): string {
  return solar.toYmdHms().slice(0, 16);
}

function toMonthFields(dayStem: Stem, ganZhi: string): { tianGanShen: string; tianGanShiShen: PreciseShen } {
  const { tianGan } = splitGanzhi(ganZhi);
  const precise = getShishen(dayStem, tianGan) as PreciseShen;
  return { tianGanShen: coarseOf(precise), tianGanShiShen: precise };
}

/** 流日/流时共同的天干十神粗分（取干支天干对日主） */
function shenFields(dayStem: Stem, ganZhi: string): { tianGanShen: string } {
  const { tianGan } = splitGanzhi(ganZhi);
  return { tianGanShen: coarseOf(getShishen(dayStem, tianGan) as PreciseShen) };
}

interface Ymd {
  y: number;
  m: number;
  d: number;
}

function toUtcMs(ymd: Ymd): number {
  return Date.UTC(ymd.y, ymd.m - 1, ymd.d);
}

/** 某年某月真实天数（公历；闰年 2 月 29） */
function daysInMonth(y: number, m: number): number {
  if (m === 2) {
    const leap = (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
    return leap ? 29 : 28;
  }
  return [4, 6, 9, 11].includes(m) ? 30 : 31;
}

function parseDate(s: string): Ymd | null {
  const parts = s.split('-');
  if (parts.length !== 3) return null;
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return null;
  if (m < 1 || m > 12 || d < 1) return null;
  // 落在当月真实天数内，防 '2026-02-31' 被 Date.UTC 静默归一化为 3-03 而产出错误 hours
  if (d > daysInMonth(y, m)) return null;
  return { y, m, d };
}

function addDays(ymd: Ymd, delta: number): Ymd {
  const dt = new Date(toUtcMs(ymd) + delta * 86_400_000);
  return { y: dt.getUTCFullYear(), m: dt.getUTCMonth() + 1, d: dt.getUTCDate() };
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function formatDate(ymd: Ymd): string {
  return `${ymd.y}-${pad2(ymd.m)}-${pad2(ymd.d)}`;
}

/** 取某 Ymd 正午的日柱（避开换日歧义，日柱一日内不变） */
function dayGanzhiOf(ymd: Ymd): string {
  return Solar.fromYmdHms(ymd.y, ymd.m, ymd.d, 12, 0, 0).getLunar().getEightChar().getDay();
}

/**
 * 从目标年约 1 月底起连续取 13 个节（立春Y .. 立春Y+1）。
 * 相邻两节把流年 Y 切成 12 个节令月：段 i（0基）= [jie[i], jie[i+1])，monthIndex = i+1。
 */
function listJieSolar(year: number): Array<{ name: string; solar: Solar }> {
  const seed = Solar.fromYmdHms(year, 1, 20, 12, 0, 0);
  const out: Array<{ name: string; solar: Solar }> = [];
  let jie = seed.getLunar().getNextJie();
  for (let i = 0; i < 13; i++) {
    out.push({ name: jie.getName(), solar: jie.getSolar() });
    jie = jie.getSolar().getLunar().getNextJie();
  }
  // 种子不变式守卫：1/20 12:00 之后的首个节必为该年 立春（大寒 Y 恒早于种子时刻）。
  // 极端年份若种子已落在次年（先得 大寒Y+1），首节会错成 立春Y+1，12 段全错 —— 宁可抛错。
  if (out.length !== 13 || out[0].name !== '立春') {
    throw new Error(`flowCalendarEngine: 节令链种子异常（year=${year}，首节=${out[0]?.name ?? '空'}），无法切分流年`);
  }
  return out;
}

function ymdOfSolar(solar: Solar): Ymd {
  return { y: solar.getYear(), m: solar.getMonth(), d: solar.getDay() };
}

export function computeFlowCalendar(opts: ComputeFlowCalendarOpts): FlowCalendarResult {
  const { year, dayStem, monthIndex, day } = opts;

  const jieList = listJieSolar(year);
  // monthIndex 越界（含 0）视为非法请求：days/hours 一律为空
  const monthInvalid = monthIndex !== undefined && (monthIndex < 1 || monthIndex > 12);
  const validMonth = monthIndex !== undefined && !monthInvalid;
  const segIndex = validMonth ? monthIndex! - 1 : -1;

  const liuYue: FlowMonth[] = [];
  for (let i = 0; i < 12; i++) {
    const start = jieList[i].solar;
    const end = jieList[i + 1].solar;
    // 节令瞬间起即属新节令月（库在精确时刻切月，已实测：立春瞬间 → 寅月）
    const monthGanzhi = start.getLunar().getEightChar().getMonth();
    const { tianGanShen, tianGanShiShen } = toMonthFields(dayStem, monthGanzhi);
    const { tianGan, diZhi } = splitGanzhi(monthGanzhi);
    liuYue.push({
      monthIndex: i + 1,
      ganzhi: monthGanzhi,
      tianGan,
      diZhi,
      termName: jieList[i].name,
      termStart: formatMinute(start),
      termEnd: formatMinute(end),
      tianGanShen,
      tianGanShiShen,
    });
  }

  let days: FlowDay[] = [];
  if (validMonth) {
    const startYmd = ymdOfSolar(jieList[segIndex].solar);
    const endYmd = ymdOfSolar(jieList[segIndex + 1].solar);
    const span = Math.round((toUtcMs(endYmd) - toUtcMs(startYmd)) / 86_400_000);
    for (let k = 0; k < span; k++) {
      const ymd = addDays(startYmd, k);
      const gz = dayGanzhiOf(ymd);
      const { tianGan, diZhi } = splitGanzhi(gz);
      days.push({
        date: formatDate(ymd),
        ganzhi: gz,
        tianGan,
        diZhi,
        tianGanShen: shenFields(dayStem, gz).tianGanShen,
      });
    }
  }

  let hours: FlowHour[] = [];
  if (day && !monthInvalid) {
    const dayYmd = parseDate(day);
    let inRange = false;
    if (dayYmd) {
      const dayMs = toUtcMs(dayYmd);
      if (validMonth) {
        const startMs = toUtcMs(ymdOfSolar(jieList[segIndex].solar));
        const endMs = toUtcMs(ymdOfSolar(jieList[segIndex + 1].solar));
        inRange = dayMs >= startMs && dayMs < endMs;
      } else {
        for (let i = 0; i < 12; i++) {
          const startMs = toUtcMs(ymdOfSolar(jieList[i].solar));
          const endMs = toUtcMs(ymdOfSolar(jieList[i + 1].solar));
          if (dayMs >= startMs && dayMs < endMs) {
            inRange = true;
            break;
          }
        }
      }
    }
    if (inRange && dayYmd) {
      for (let i = 1; i <= 12; i++) {
        const sampleHour = (i - 1) * 2;
        const hourGanzhi = Solar.fromYmdHms(dayYmd.y, dayYmd.m, dayYmd.d, sampleHour, 1, 0)
          .getLunar()
          .getEightChar()
          .getTime();
        const { tianGan, diZhi } = splitGanzhi(hourGanzhi);
        hours.push({
          hourIndex: i,
          hourLabel: HOUR_LABELS[i - 1],
          ganzhi: hourGanzhi,
          tianGan,
          diZhi,
          tianGanShen: shenFields(dayStem, hourGanzhi).tianGanShen,
        });
      }
    }
  }

  return { year, liuYue, days, hours };
}
